import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    ArrowLeft, ClipboardList, Plus, Trash2, Save, Search, Minus, Wrench, Weight
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import useFormDraft from '../hooks/useFormDraft';
import { useResilientSave, isNetworkError } from '../components/OfflineSavePrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { value: 'encombrant', label: 'Encombrant' },
    { value: 'dechetterie', label: 'Dechetterie' },
    { value: 'dem_2', label: 'Dem 2' },
    { value: 'cave_garage', label: 'Cave/Garage' },
    { value: 'charge_2', label: 'Charge 2' },
    { value: 'reste_non_inclus', label: 'Reste/Non inclus' },
];

const SUGGESTIONS = [
    'Canape 3 places', 'Canape 2 places', 'Table basse', 'Meuble TV', 'Fauteuil',
    'Bibliotheque', 'Tapis', 'Lampadaire', 'Table a manger', 'Chaises',
    'Lit double', 'Lit simple', 'Matelas', 'Table de chevet', 'Armoire 2 portes',
    'Armoire 3 portes', 'Commode', 'Bureau', 'Chaise de bureau', 'Miroir',
    'Refrigerateur', 'Congelateur', 'Lave-vaisselle', 'Micro-ondes', 'Four',
    'Machine a laver', 'Seche-linge', 'Meuble vasque', 'Etagere',
    'Imprimante', 'Ecran PC', 'Carton standard', 'Carton livre', 'Carton fragile',
    'Carton barrel', 'Penderie', 'Divers',
    'Velo', 'Plantes', 'Tableau', 'Aspirateur', 'Valise', 'Robot cuisine',
];

const DEFAULT_VOLUMES = {
    'Carton standard': 0.1, 'Carton livre': 0.05, 'Carton fragile': 0.1, 'Carton barrel': 0.3, 'Penderie': 0.4,
};

export default function Inventaire() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [items, setItems] = useState([]);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // Form state
    const [searchTerm, setSearchTerm] = useState('');
    const [newVolume, setNewVolume] = useState('');
    const [newQty, setNewQty] = useState(1);

    // ----- Offline-resilience -----
    const draft = useFormDraft(`inventaire:${id}`, items, setItems);
    const resilient = useResilientSave();
    const [showRestore, setShowRestore] = useState(false);
    useEffect(() => {
        if (draft.hasDraft) setShowRestore(true);
    }, [draft.hasDraft]);
    const [newCategory, setNewCategory] = useState('dem_1');
    const [newDemontage, setNewDemontage] = useState(false);
    const [newObjetLourd, setNewObjetLourd] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const filteredSuggestions = searchTerm.trim()
        ? SUGGESTIONS.filter(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
        : [];

    const totalVolume = items.reduce((sum, item) => sum + ((parseFloat(item.volume) || 0) * (parseInt(item.quantity) || 0)), 0);
    const totalObjects = items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);

    const fetchEvent = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/events/${id}`, { withCredentials: true });
            setEvent(res.data);
        } catch { toast.error('Erreur chargement evenement'); }
    }, [id]);

    const fetchInventory = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/events/${id}/inventaire`, { withCredentials: true });
            if (res.data?.rooms?.length > 0) {
                const allItems = res.data.rooms.flatMap(r => r.items || []);
                if (allItems.length > 0) setItems(allItems);
            }
        } catch { /* no inventory yet */ }
        finally { setIsDirty(false); }
    }, [id]);

    useEffect(() => { fetchEvent(); fetchInventory(); }, [fetchEvent, fetchInventory]);

    const handleSelectSuggestion = (name) => {
        setSearchTerm(name);
        const defaultVol = DEFAULT_VOLUMES[name];
        if (defaultVol !== undefined) setNewVolume(defaultVol.toString());
        setShowSuggestions(false);
    };

    const handleAddItem = () => {
        if (!searchTerm.trim()) { toast.error('Entrez le nom de l\'article'); return; }
        const vol = parseFloat(newVolume) || 0;
        if (vol <= 0) { toast.error('Le volume doit etre superieur a 0'); return; }
        const newItem = {
            name: searchTerm.trim(),
            volume: vol,
            quantity: parseInt(newQty) || 1,
            category: newCategory,
            demontage: newDemontage,
            objet_lourd: newObjetLourd,
            note: '',
        };
        setItems(prev => [newItem, ...prev]);
        setIsDirty(true);
        setSearchTerm('');
        setNewVolume('');
        setNewQty(1);
        setNewCategory('dem_1');
        setNewDemontage(false);
        setNewObjetLourd(false);
        toast.success(`${newItem.name} ajoute`);
    };

    const handleRemoveItem = (idx) => {
        setItems(prev => prev.filter((_, i) => i !== idx));
        setIsDirty(true);
    };

    const handleUpdateQty = (idx, delta) => {
        setItems(prev => prev.map((item, i) => {
            if (i !== idx) return item;
            return { ...item, quantity: Math.max(0, (parseInt(item.quantity) || 0) + delta) };
        }));
        setIsDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const rooms = [{ name: 'Inventaire', items }];
            const r = await resilient.trySave(() => axios.put(`${API_URL}/api/events/${id}/inventaire`, { rooms }, { withCredentials: true }));
            if (!r.ok) {
                if (!r.offline) toast.error('Erreur sauvegarde');
                return;
            }
            setIsDirty(false);
            draft.clear();
            toast.success('Inventaire sauvegarde');
        } catch (err) {
            if (!isNetworkError(err)) toast.error('Erreur sauvegarde');
        } finally { setSaving(false); }
    };

    // Warn before leaving if there are unsaved changes
    useEffect(() => {
        const beforeUnload = (e) => {
            if (!isDirty) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [isDirty]);

    const handleBack = () => {
        if (isDirty && !window.confirm("Vous avez des modifications non enregistrées.\nVoulez-vous vraiment quitter sans enregistrer ?")) {
            return;
        }
        navigate(`/agenda/${id}`);
    };

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            {resilient.Modal}
            {showRestore && (
                <div className="fixed top-20 right-4 z-50 bg-white border-2 border-amber-300 rounded-xl shadow-lg p-4 max-w-sm" data-testid="inventaire-draft-prompt">
                    <p className="text-sm font-semibold text-[#1B365D] mb-1">Brouillon non envoyé</p>
                    <p className="text-xs text-gray-500 mb-3">Un inventaire partiel a été conservé localement.</p>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { draft.clear(); setShowRestore(false); }}>Ignorer</Button>
                        <Button size="sm" className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white" onClick={() => { draft.restore(); setShowRestore(false); setIsDirty(true); }} data-testid="inventaire-draft-restore">Restaurer</Button>
                    </div>
                </div>
            )}
            <div className="max-w-7xl mx-auto" data-testid="inventaire-page">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <button onClick={handleBack}
                            className="w-10 h-10 rounded-xl bg-white border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F8FAFC] transition-colors"
                            data-testid="back-to-event">
                            <ArrowLeft className="w-5 h-5 text-[#1B365D]" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-[#1B365D] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="inventaire-title">
                                Calculateur de volume
                            </h1>
                            {event && <p className="text-sm text-[#475569] mt-0.5">{event.prospect?.nom || event.title}</p>}
                        </div>
                    </div>
                    <Button onClick={handleSave} disabled={saving}
                        className="bg-[#1B365D] hover:bg-[#112240] text-white font-medium rounded-xl px-6 h-11 gap-2 shadow-sm"
                        data-testid="save-inventory">
                        <Save className="w-4 h-4" /> {saving ? 'Sauvegarde...' : 'Enregistrer'}
                    </Button>
                </div>

                {/* Bento Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">

                    {/* LEFT: Add Form */}
                    <div className="lg:col-span-5 space-y-6">
                        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-5 sm:p-6" data-testid="add-form">
                            <h2 className="text-lg font-semibold text-[#0F172A] mb-4">Ajouter un article</h2>

                            {/* Search */}
                            <div className="relative mb-3">
                                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Article</label>
                                <div className="relative">
                                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                                    <input
                                        value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setShowSuggestions(true); }}
                                        onFocus={() => setShowSuggestions(true)}
                                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                        placeholder="Rechercher un article..."
                                        className="w-full bg-white border border-[#E2E8F0] rounded-xl pl-10 pr-4 py-2.5 text-[#0F172A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20 focus:border-[#1B365D] transition-all"
                                        data-testid="search-item"
                                    />
                                </div>
                                {showSuggestions && filteredSuggestions.length > 0 && (
                                    <div className="absolute z-20 w-full mt-1.5 bg-white border border-[#E2E8F0] rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                        {filteredSuggestions.slice(0, 8).map((s, i) => (
                                            <button key={i} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F8FAFC] text-[#0F172A] transition-colors first:rounded-t-xl last:rounded-b-xl flex items-center justify-between"
                                                onMouseDown={() => handleSelectSuggestion(s)}>
                                                <span>{s}</span>
                                                {DEFAULT_VOLUMES[s] && <span className="text-xs text-[#94A3B8]">{DEFAULT_VOLUMES[s]} m³</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Volume + Quantity + Add Button - compact row */}
                            <div className="flex items-end gap-2 mb-4">
                                <div className="flex-1 min-w-0">
                                    <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Volume (m³)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={newVolume}
                                        onChange={(e) => setNewVolume(e.target.value)}
                                        placeholder="0.00"
                                        className="w-full bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5 text-[#0F172A] text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D]/20 focus:border-[#1B365D] transition-all"
                                        data-testid="volume-input"
                                    />
                                </div>
                                <div className="w-[150px] shrink-0">
                                    <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Qte</label>
                                    <div className="flex items-center border border-[#E2E8F0] rounded-xl bg-white overflow-hidden h-[48px]">
                                        <button
                                            type="button"
                                            className="px-3.5 h-full text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] active:bg-[#E2E8F0] transition-colors"
                                            onClick={() => setNewQty(Math.max(1, newQty - 1))}
                                            aria-label="Diminuer la quantité"
                                            data-testid="qty-minus"
                                        >
                                            <Minus className="w-5 h-5" />
                                        </button>
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={newQty}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                if (raw === '') { setNewQty(1); return; }
                                                const n = parseInt(raw, 10);
                                                if (!isNaN(n) && n >= 1) setNewQty(n);
                                            }}
                                            className="flex-1 w-0 text-center font-semibold text-[#0F172A] text-base bg-transparent border-0 outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            data-testid="qty-value"
                                        />
                                        <button
                                            type="button"
                                            className="px-3.5 h-full text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] active:bg-[#E2E8F0] transition-colors"
                                            onClick={() => setNewQty(newQty + 1)}
                                            aria-label="Augmenter la quantité"
                                            data-testid="qty-plus"
                                        >
                                            <Plus className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={handleAddItem}
                                    className="shrink-0 bg-[#1B365D] hover:bg-[#112240] text-white font-medium rounded-xl px-5 h-[48px] transition-colors flex items-center justify-center gap-1.5 shadow-sm text-sm"
                                    data-testid="add-item-btn">
                                    <Plus className="w-4 h-4" />
                                    Ajouter
                                </button>
                            </div>

                            {/* Separator */}
                            <div className="border-t border-[#E2E8F0] my-4" />

                            {/* Category Cards - Dem 1 is default */}
                            <div className="mb-4">
                                <label className="block text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">
                                    Categorie <span className="normal-case font-normal text-[#94A3B8]">(Dem 1 par defaut)</span>
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {CATEGORIES.map((cat) => (
                                        <button key={cat.value}
                                            onClick={() => setNewCategory(prev => prev === cat.value ? 'dem_1' : cat.value)}
                                            className={`flex items-center justify-center px-2 py-2 cursor-pointer rounded-lg border text-xs font-medium transition-all ${
                                                newCategory === cat.value
                                                    ? 'border-[#1B365D] bg-[#1B365D]/5 text-[#1B365D] ring-1 ring-[#1B365D]'
                                                    : 'border-[#E2E8F0] bg-white text-[#475569] hover:border-[#1B365D]/30'
                                            }`}
                                            data-testid={`cat-${cat.value}`}>
                                            {cat.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Checkbox Cards */}
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => setNewDemontage(!newDemontage)}
                                    className={`flex items-center gap-2.5 p-3 rounded-lg border transition-all text-left ${
                                        newDemontage
                                            ? 'border-[#1B365D] bg-[#1B365D]/5 ring-1 ring-[#1B365D]'
                                            : 'border-[#E2E8F0] bg-white hover:border-[#1B365D]/30'
                                    }`}
                                    data-testid="check-demontage">
                                    <Wrench className={`w-4 h-4 shrink-0 ${newDemontage ? 'text-[#C9A962]' : 'text-[#94A3B8]'}`} />
                                    <div>
                                        <p className={`text-xs font-medium leading-tight ${newDemontage ? 'text-[#1B365D]' : 'text-[#475569]'}`}>Demontage</p>
                                        <p className="text-[10px] text-[#94A3B8] leading-tight">Remontage inclus</p>
                                    </div>
                                </button>
                                <button
                                    onClick={() => setNewObjetLourd(!newObjetLourd)}
                                    className={`flex items-center gap-2.5 p-3 rounded-lg border transition-all text-left ${
                                        newObjetLourd
                                            ? 'border-[#1B365D] bg-[#1B365D]/5 ring-1 ring-[#1B365D]'
                                            : 'border-[#E2E8F0] bg-white hover:border-[#1B365D]/30'
                                    }`}
                                    data-testid="check-lourd">
                                    <Weight className={`w-4 h-4 shrink-0 ${newObjetLourd ? 'text-[#C9A962]' : 'text-[#94A3B8]'}`} />
                                    <div>
                                        <p className={`text-xs font-medium leading-tight ${newObjetLourd ? 'text-[#1B365D]' : 'text-[#475569]'}`}>Objet lourd</p>
                                        <p className="text-[10px] text-[#94A3B8] leading-tight">Manutention speciale</p>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: Table + Total */}
                    <div className="lg:col-span-7 space-y-6">
                        {/* Total Volume Card */}
                        <div className="bg-[#1B365D] text-white rounded-2xl p-6 flex items-center justify-between shadow-lg" data-testid="total-volume-card">
                            <div>
                                <p className="text-[#94A3B8] text-xs font-semibold uppercase tracking-wider mb-1">Volume total</p>
                                <p className="text-sm text-white/70">{totalObjects} article{totalObjects !== 1 ? 's' : ''} | {items.length} ligne{items.length !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-4xl font-bold text-white flex items-baseline gap-1.5" data-testid="total-volume">
                                    {totalVolume.toFixed(2)}
                                    <span className="text-xl font-normal text-[#C9A962]">m³</span>
                                </p>
                            </div>
                        </div>

                        {/* Items Table */}
                        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid="items-table">
                            {items.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 px-6">
                                    <div className="w-16 h-16 rounded-2xl bg-[#F1F5F9] flex items-center justify-center mb-4">
                                        <ClipboardList className="w-7 h-7 text-[#94A3B8]" />
                                    </div>
                                    <p className="text-sm font-medium text-[#475569]">Aucun article</p>
                                    <p className="text-xs text-[#94A3B8] mt-1">Utilisez le formulaire pour ajouter des articles</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                                                <th className="px-5 py-3.5 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">Article</th>
                                                <th className="px-3 py-3.5 text-center text-xs font-semibold text-[#475569] uppercase tracking-wider">Vol.</th>
                                                <th className="px-3 py-3.5 text-center text-xs font-semibold text-[#475569] uppercase tracking-wider">Qte</th>
                                                <th className="px-3 py-3.5 text-center text-xs font-semibold text-[#475569] uppercase tracking-wider">Total</th>
                                                <th className="px-3 py-3.5 w-10"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((item, idx) => {
                                                const itemTotal = (parseFloat(item.volume) || 0) * (parseInt(item.quantity) || 0);
                                                const catLabel = item.category === 'dem_1' ? 'Dem 1' : (CATEGORIES.find(c => c.value === item.category)?.label || '');
                                                return (
                                                    <tr key={idx} className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#F8FAFC]/50 transition-colors group"
                                                        data-testid={`item-row-${idx}`}>
                                                        <td className="px-5 py-3.5">
                                                            <p className="text-sm font-medium text-[#0F172A]">{item.name}</p>
                                                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                                {catLabel && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#1B365D]/10 text-[#1B365D]">
                                                                        {catLabel}
                                                                    </span>
                                                                )}
                                                                {item.demontage && (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700">
                                                                        <Wrench className="w-2.5 h-2.5" /> D/R
                                                                    </span>
                                                                )}
                                                                {item.objet_lourd && (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-600">
                                                                        <Weight className="w-2.5 h-2.5" /> Lourd
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-3.5 text-center text-sm text-[#475569]">
                                                            {(parseFloat(item.volume) || 0).toFixed(2)}
                                                        </td>
                                                        <td className="px-3 py-3.5">
                                                            <div className="flex items-center justify-center gap-0">
                                                                <button className="w-6 h-6 rounded-l-lg border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] flex items-center justify-center text-xs transition-colors"
                                                                    onClick={() => handleUpdateQty(idx, -1)}>-</button>
                                                                <span className="w-8 h-6 border-t border-b border-[#E2E8F0] flex items-center justify-center text-xs font-semibold text-[#0F172A] bg-white">{item.quantity}</span>
                                                                <button className="w-6 h-6 rounded-r-lg border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] flex items-center justify-center text-xs transition-colors"
                                                                    onClick={() => handleUpdateQty(idx, 1)}>+</button>
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-3.5 text-center">
                                                            <span className="text-sm font-semibold text-[#1B365D]">{itemTotal.toFixed(2)}</span>
                                                        </td>
                                                        <td className="px-3 py-3.5">
                                                            <button className="text-[#EF4444] hover:bg-[#FEF2F2] rounded-lg p-1.5 transition-colors opacity-0 group-hover:opacity-100"
                                                                onClick={() => handleRemoveItem(idx)} data-testid={`remove-item-${idx}`}>
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Save at bottom on mobile */}
                        <div className="lg:hidden">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="w-full bg-[#1B365D] hover:bg-[#112240] text-white font-medium rounded-xl px-6 py-3.5 transition-colors flex items-center justify-center gap-2 shadow-sm text-sm disabled:opacity-50"
                                data-testid="save-inventory-mobile">
                                <Save className="w-4 h-4" />
                                {saving ? 'Sauvegarde...' : 'Enregistrer l\'inventaire'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
